import { CitationSource } from 'types/chat-message'
import { AgentAction } from 'types/agent'

const SOURCES_DELIMITER = '<<<SOURCES>>>'
const ACTION_DELIMITER = '<<<ACTION_REQUIRED>>>'

export type ParsedMessage = {
  text: string
  sources: Record<string, CitationSource> | null
}

export function parseMessageWithSources(rawMessage: string): ParsedMessage {
  const delimiterIndex = rawMessage.indexOf(SOURCES_DELIMITER)

  if (delimiterIndex !== -1) {
    const text = rawMessage.substring(0, delimiterIndex).trim()
    const sourcesJson = rawMessage.substring(delimiterIndex + SOURCES_DELIMITER.length).trim()

    try {
      const sources = JSON.parse(sourcesJson) as Record<string, CitationSource>
      return { text, sources }
    } catch {
      return { text, sources: null }
    }
  }

  // Strip partial delimiter during streaming (e.g. "<<<SOUR" at the end)
  const partialMatch = rawMessage.match(/\n*<<<[A-Z>]*$/)
  if (partialMatch && partialMatch.index !== undefined) {
    return { text: rawMessage.substring(0, partialMatch.index).trim(), sources: null }
  }

  return { text: rawMessage, sources: null }
}

export function stripCitationMarkers(text: string): string {
  // [N] remote source, [img:N] image, [att:N] user-uploaded attachment.
  return text.replace(/\[(?:img:|att:)?\d+\]/g, '').trim()
}

export function getPlainMessageText(rawMessage: string): string {
  return stripCitationMarkers(parseMessageWithSources(rawMessage).text)
}

export function processCitationMarkers(text: string): string {
  return (
    text
      // Normalize citation markers
      .replace(/\[img:\s*(\d+)\s*\]/g, '[img:$1]')
      .replace(/\[att:\s*(\d+)\s*\]/g, '[att:$1]')
      .replace(/\[\s*(\d+)\s*\]/g, '[$1]')
      // Collapse 3+ newlines into 2 (max one blank line)
      .replace(/\n{3,}/g, '\n\n')
      // Remove trailing whitespace from lines
      .replace(/[ \t]+$/gm, '')
      // Remove extra spaces (but keep single spaces)
      .replace(/ {2,}/g, ' ')
  )
}

export type ParsedAgentMessage = {
  /** Conversational text shown before the action card. */
  text: string
  /** The pending action, once its JSON has fully arrived (null while streaming). */
  action: AgentAction | null
  /** Continuation streamed after the action was resolved; non-empty => resolved. */
  after: string
}

/**
 * Extracts the bounds of the first complete JSON object starting at `from`,
 * tracking brace depth and skipping braces inside strings. Returns the end index
 * (exclusive) or -1 if the object is not yet complete (still streaming).
 */
function findJsonEnd(text: string, from: number): number {
  let depth = 0
  let inString = false
  let escaped = false

  for (let i = from; i < text.length; i++) {
    const ch = text[i]
    if (inString) {
      if (escaped) escaped = false
      else if (ch === '\\') escaped = true
      else if (ch === '"') inString = false
      continue
    }
    if (ch === '"') inString = true
    else if (ch === '{') depth++
    else if (ch === '}') {
      depth--
      if (depth === 0) return i + 1
    }
  }
  return -1
}

/**
 * Parses an agentic bot message into { text, action, after }.
 * - No marker: returns the text as-is (a trailing partial marker is stripped so
 *   it never flashes during streaming).
 * - Marker present, JSON incomplete: shows the text before it, no action yet.
 * - Marker + full JSON: returns the parsed action and any continuation (`after`).
 */
export function parseAgentAction(raw: string): ParsedAgentMessage {
  const at = raw.indexOf(ACTION_DELIMITER)

  if (at === -1) {
    // Strip a partial delimiter forming at the end while streaming.
    const partial = raw.match(/\n*<<<[A-Z_>]*$/)
    if (partial && partial.index !== undefined) {
      return { text: raw.slice(0, partial.index).trimEnd(), action: null, after: '' }
    }
    return { text: raw, action: null, after: '' }
  }

  const text = raw.slice(0, at).trimEnd()
  const rest = raw.slice(at + ACTION_DELIMITER.length)
  const start = rest.indexOf('{')
  if (start === -1) return { text, action: null, after: '' }

  const end = findJsonEnd(rest, start)
  if (end === -1) return { text, action: null, after: '' }

  try {
    const action = JSON.parse(rest.slice(start, end)) as AgentAction
    const after = rest.slice(end).trim()
    return { text, action, after }
  } catch {
    return { text, action: null, after: '' }
  }
}
